import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Search,
  MessageSquare,
  Send,
  Bot,
  User as UserIcon,
  Headphones,
  X,
  CheckCircle2,
  RotateCcw,
  ArrowLeftRight,
  Lock,
  Info,
  Monitor,
  Globe,
  Copy,
  Clock,
  Filter,
} from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const STATUS_META = {
  ai: { label: "IA", color: "#F5C542" },
  pending_human: { label: "Pendente", color: "#FFD84D" },
  assigned: { label: "Em atendimento", color: "#22c55e" },
  closed: { label: "Encerrado", color: "#8b8b8b" },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.ai;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{ color: m.color, background: `${m.color}18`, border: `1px solid ${m.color}55` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color, boxShadow: `0 0 6px ${m.color}` }} />
      {m.label}
    </span>
  );
};

const fmt = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};
const timeAgo = (iso) => {
  if (!iso) return "-";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text || ""); toast.success("Copiado"); } catch { toast.error("Falha ao copiar"); }
};

const AVATAR = "/support-avatar.png?v=3";

// -------------------------------------------------- Message
const AdminBubble = ({ msg }) => {
  if (msg.sender === "system") {
    return (
      <div className="my-2 text-center">
        <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest text-white/50 bg-white/[0.04] border border-white/10">
          {msg.text}
        </span>
      </div>
    );
  }
  const align = msg.sender === "admin" ? "justify-end" : "justify-start";
  const isUser = msg.sender === "user";
  const isAI = msg.sender === "ai";
  const isAdmin = msg.sender === "admin";
  return (
    <div className={`flex ${align} mb-2.5`}>
      <div className="max-w-[80%] flex gap-2 items-end">
        {!isAdmin && (
          isUser ? (
            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <UserIcon className="w-3.5 h-3.5 text-white/70" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0"
                 style={{ border: "1.5px solid rgba(245, 197, 66,0.55)", boxShadow: "0 0 8px rgba(245, 197, 66,0.3)" }}>
              <img src={AVATAR} alt="" className="w-full h-full object-cover" />
            </div>
          )
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
               style={{ color: isAI ? "#F5C542cc" : isUser ? "#ffffff88" : "#22c55e" }}>
            {isAI ? "Suporte Virtual" : isUser ? "Cliente" : (msg.author || "Atendente")}
          </div>
          <div className={`px-3 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-line break-words ${isAdmin ? "text-black" : "text-white"}`}
               style={{
                 background: isAdmin ? "linear-gradient(135deg,#22c55e,#16a34a)" : isAI ? "rgba(245, 197, 66,0.08)" : "rgba(255,255,255,0.05)",
                 border: isAI ? "1px solid rgba(245, 197, 66,0.25)" : "1px solid rgba(255,255,255,0.08)",
               }}>
            {msg.text}
          </div>
          <div className="text-[10px] text-white/30 mt-0.5 text-right">{fmt(msg.at)}</div>
        </div>
        {isAdmin && (
          <div className="w-8 h-8 rounded-full overflow-hidden shrink-0"
               style={{ border: "1.5px solid #22c55e", boxShadow: "0 0 8px rgba(34,197,94,0.4)" }}>
            <img src={AVATAR} alt="" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------- Ticket List Item
const TicketRow = ({ t, active, onOpen }) => {
  const last = t.messages?.[t.messages.length - 1];
  return (
    <button
      onClick={() => onOpen(t.id)}
      className={`w-full text-left rounded-xl p-3 border transition-colors ${active ? "bg-[#F5C542]/8 border-[#F5C542]/40" : "bg-white/[0.02] border-white/[0.06] hover:border-white/20"}`}
      data-testid={`ticket-row-${t.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <StatusPill status={t.status} />
        {t.unread_admin > 0 && (
          <span className="ml-auto text-[10px] font-bold text-black bg-[#F5C542] rounded-full px-1.5 py-0.5">
            {t.unread_admin > 9 ? "9+" : t.unread_admin}
          </span>
        )}
        <span className={`text-[10px] text-white/40 flex items-center gap-0.5 ${t.unread_admin > 0 ? "" : "ml-auto"}`}>
          <Clock className="w-2.5 h-2.5" /> {timeAgo(t.updated_at || t.created_at)}
        </span>
      </div>
      <div className="text-white text-sm font-semibold truncate">
        {t.customer?.name || t.customer?.email || t.customer?.username || "Sem identificação"}
      </div>
      <div className="text-white/45 text-[11px] font-mono">{t.protocol}</div>
      {last && (
        <div className="text-white/55 text-[12px] mt-1 line-clamp-1">
          <span className="text-white/35">
            {last.sender === "user" ? "" : last.sender === "ai" ? "IA: " : last.sender === "admin" ? "Você: " : ""}
          </span>
          {last.text}
        </div>
      )}
    </button>
  );
};

// -------------------------------------------------- Main
export const SupportManager = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ ai: 0, pending_human: 0, assigned: 0, closed: 0 });
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const params = {};
      if (filter !== "all" && filter !== "open") params.status = filter;
      if (q.trim()) params.q = q.trim();
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/tickets", { params }),
        api.get("/admin/tickets/stats"),
      ]);
      const filtered = filter === "open" ? list.filter((t) => t.status !== "closed") : list;
      setItems(filtered);
      setStats(s);
    } catch {
      toast.error("Erro ao carregar atendimentos");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [filter, q]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    let stopped = false;
    const load1 = async () => {
      try {
        const { data } = await api.get(`/admin/tickets/${selectedId}`);
        if (!stopped) setSelected(data);
      } catch { if (!stopped) setSelected(null); }
    };
    load1();
    const t = setInterval(load1, 4000);
    return () => { stopped = true; clearInterval(t); };
  }, [selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [selected?.messages?.length]);

  const totalPending = stats.pending_human;

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || !selected) return;
    setSending(true);
    setInput("");
    try {
      await api.post(`/admin/tickets/${selected.id}/message`, { text });
      const { data } = await api.get(`/admin/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally { setSending(false); }
  };

  const assume = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/assume`);
      toast.success("Atendimento assumido");
      const { data } = await api.get(`/admin/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch { toast.error("Erro ao assumir"); }
  };

  const returnAI = async () => {
    if (!selected || !window.confirm("Devolver este atendimento para o assistente virtual?")) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/return-ai`);
      toast.success("Devolvido à IA");
      const { data } = await api.get(`/admin/tickets/${selected.id}`); setSelected(data); load();
    } catch { toast.error("Erro ao devolver"); }
  };

  const transfer = async () => {
    if (!selected) return;
    const to = window.prompt("E-mail do administrador para transferir:");
    if (!to) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/transfer`, { to_email: to.trim() });
      toast.success("Transferido");
      const { data } = await api.get(`/admin/tickets/${selected.id}`); setSelected(data); load();
    } catch { toast.error("Erro ao transferir"); }
  };

  const close = async () => {
    if (!selected) return;
    const reason = window.prompt("Motivo do encerramento (opcional):") || "";
    setClosing(true);
    try {
      await api.post(`/admin/tickets/${selected.id}/close`, { reason });
      toast.success("Atendimento encerrado");
      const { data } = await api.get(`/admin/tickets/${selected.id}`); setSelected(data); load();
    } catch { toast.error("Erro ao encerrar"); }
    finally { setClosing(false); }
  };

  const reopen = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/reopen`);
      toast.success("Reaberto");
      const { data } = await api.get(`/admin/tickets/${selected.id}`); setSelected(data); load();
    } catch { toast.error("Erro ao reabrir"); }
  };

  const isClosed = selected?.status === "closed";
  const isAI = selected?.status === "ai";

  const StatCard = ({ label, value, color }) => (
    <div className="rounded-2xl p-4 border" style={{ background: "linear-gradient(160deg, #131313, #0b0b0b)", borderColor: `${color}33` }}>
      <div className="text-[10px] uppercase tracking-widest text-white/45 font-bold">{label}</div>
      <div className="mt-1 font-display font-black text-2xl" style={{ color }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="admin-support">
      <PageTitle title="Central de Atendimento"
                 subtitle="IA responde primeiro • Escalonamento humano automático • Todos os atendimentos são registrados"
                 action={
                   <div className="flex items-center gap-2">
                     {totalPending > 0 && (
                       <span className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full"
                             style={{ color: "#FFD84D", background: "rgba(255, 216, 77,0.15)", border: "1px solid rgba(255, 216, 77,0.55)" }}>
                         {totalPending} aguardando
                       </span>
                     )}
                   </div>
                 } />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Com IA" value={stats.ai} color="#F5C542" />
        <StatCard label="Pendente humano" value={stats.pending_human} color="#FFD84D" />
        <StatCard label="Em atendimento" value={stats.assigned} color="#22c55e" />
        <StatCard label="Encerrados" value={stats.closed} color="#8b8b8b" />
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-5">
        {/* List column */}
        <Card className="!p-3 lg:sticky lg:top-6 self-start max-h-[80vh] overflow-hidden flex flex-col">
          <div className="p-1 pb-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className={`${inputCls} pl-9`}
                placeholder="Protocolo, nome, e-mail, IP..."
                data-testid="ticket-search"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                ["open", "Ativos"],
                ["pending_human", "Pendentes"],
                ["assigned", "Meus"],
                ["ai", "IA"],
                ["closed", "Encerrados"],
                ["all", "Todos"],
              ].map(([v, label]) => (
                <button key={v}
                        onClick={() => setFilter(v)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${filter === v ? "bg-[#F5C542]/10 text-[#F5C542] border-[#F5C542]/45" : "bg-white/[0.03] text-white/55 border-white/10 hover:text-white"}`}
                        data-testid={`ticket-filter-${v}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pb-1">
            {items.length === 0 ? (
              <div className="text-center text-white/40 text-xs py-8">Nenhum atendimento.</div>
            ) : (
              items.map((t) => <TicketRow key={t.id} t={t} active={selectedId === t.id} onOpen={setSelectedId} />)
            )}
          </div>
        </Card>

        {/* Chat column */}
        {!selected ? (
          <Card className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                 style={{ background: "rgba(245, 197, 66,0.12)", border: "1px solid rgba(245, 197, 66,0.4)" }}>
              <MessageSquare className="w-7 h-7 text-[#F5C542]" />
            </div>
            <div className="font-display font-bold text-white">Selecione um atendimento</div>
            <div className="mt-1 text-sm text-white/50">A lista atualiza automaticamente a cada 5 segundos.</div>
          </Card>
        ) : (
          <Card className="!p-0 overflow-hidden flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-white">
                    {selected.customer?.name || "Sem nome"}
                  </div>
                  <StatusPill status={selected.status} />
                  <span className="text-[10px] font-mono text-white/50">{selected.protocol}</span>
                  <button onClick={() => copyText(selected.protocol)} className="text-white/40 hover:text-white transition-colors">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div className="text-[11px] text-white/45 mt-0.5">
                  {[selected.customer?.email, selected.customer?.username && "@" + selected.customer.username].filter(Boolean).join(" • ")}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(selected.status === "pending_human" || selected.status === "ai") && (
                  <button onClick={assume}
                          className="rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-black"
                          style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)" }}
                          data-testid="ticket-assume">
                    <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Assumir
                  </button>
                )}
                {!isClosed && (
                  <>
                    <button onClick={returnAI} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/[0.04] border border-white/10 inline-flex items-center gap-1" data-testid="ticket-return-ai">
                      <RotateCcw className="w-3.5 h-3.5" /> IA
                    </button>
                    <button onClick={transfer} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/[0.04] border border-white/10 inline-flex items-center gap-1">
                      <ArrowLeftRight className="w-3.5 h-3.5" /> Transferir
                    </button>
                    <button onClick={close} disabled={closing} className="rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white" style={{ background: "linear-gradient(135deg,#ef4444,#b91c1c)" }} data-testid="ticket-close">
                      <Lock className="w-3.5 h-3.5 inline mr-1" /> Encerrar
                    </button>
                  </>
                )}
                {isClosed && (
                  <button onClick={reopen} className="rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-black" style={{ background: "linear-gradient(135deg,#F5C542,#F28C28)" }}>
                    Reabrir
                  </button>
                )}
                <button onClick={() => setShowInfo((v) => !v)}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/[0.04] border border-white/10 inline-flex items-center gap-1"
                        data-testid="ticket-info-toggle">
                  <Info className="w-3.5 h-3.5" /> Info
                </button>
              </div>
            </div>

            {/* Body */}
            <div className={`flex-1 grid ${showInfo ? "md:grid-cols-[1fr_320px]" : "grid-cols-1"} min-h-0`}>
              <div className="flex flex-col min-h-0">
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-black/20" data-testid="ticket-messages">
                  {(selected.messages || []).map((m) => <AdminBubble key={m.id} msg={m} />)}
                </div>
                {isClosed ? (
                  <div className="px-4 py-3 border-t border-white/[0.06] text-xs text-white/45 flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5" /> Encerrado em {fmt(selected.closed_at)} — {selected.close_reason || "sem motivo registrado"}
                  </div>
                ) : (
                  <form onSubmit={send} className="px-3 py-3 border-t border-white/[0.06] flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                      rows={1}
                      placeholder={isAI ? "Ao enviar, você assumirá este atendimento..." : "Sua resposta..."}
                      className="flex-1 bg-white/[0.03] border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5C542]/50 resize-none max-h-32"
                      data-testid="ticket-input"
                    />
                    <button type="submit" disabled={sending || !input.trim()}
                            className="rounded-full px-4 h-11 font-bold uppercase tracking-wider text-black text-xs disabled:opacity-50 flex items-center gap-1.5"
                            style={{ background: "linear-gradient(135deg,#F5C542,#F28C28)" }}
                            data-testid="ticket-send">
                      <Send className="w-4 h-4" /> Enviar
                    </button>
                  </form>
                )}
              </div>

              {showInfo && (
                <aside className="border-l border-white/[0.06] p-4 overflow-y-auto bg-black/30 text-[12px]" data-testid="ticket-info">
                  <div className="font-display font-bold text-white mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#F5C542]" /> Detalhes
                  </div>
                  <Section title="Cliente">
                    <KV label="Nome" value={selected.customer?.name || "—"} />
                    <KV label="E-mail" value={selected.customer?.email || "—"} copyable />
                    <KV label="Usuário" value={selected.customer?.username || "—"} />
                  </Section>
                  <Section title="Chamado">
                    <KV label="Protocolo" value={selected.protocol} copyable />
                    <KV label="Categoria" value={selected.category} />
                    <KV label="Prioridade" value={selected.priority || "media"} />
                    <KV label="Aberto em" value={fmt(selected.created_at)} />
                    {selected.assigned_admin && <KV label="Atendente" value={selected.assigned_admin} />}
                    {selected.assigned_at && <KV label="Assumido em" value={fmt(selected.assigned_at)} />}
                    {selected.closed_at && <KV label="Encerrado em" value={fmt(selected.closed_at)} />}
                    {selected.close_reason && <KV label="Motivo" value={selected.close_reason} />}
                  </Section>
                  <Section title={<span className="inline-flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5" /> Técnico</span>}>
                    {Object.entries(selected.tech || {}).map(([k, v]) => (
                      <KV key={k} label={k} value={v || "—"} copyable={["ip", "ua", "referrer", "url"].includes(k)} />
                    ))}
                  </Section>
                  <Section title="Auditoria">
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {(selected.audit || []).map((a, i) => (
                        <div key={i} className="text-[11px] text-white/55">
                          <span className="text-[#F5C542]">{a.event}</span>
                          <span className="text-white/35"> • {fmt(a.at)}</span>
                          <div className="text-white/40">{a.actor}{a.detail ? ` — ${a.detail}` : ""}</div>
                        </div>
                      ))}
                    </div>
                  </Section>
                </aside>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-4">
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

const KV = ({ label, value, copyable }) => (
  <div className="flex items-start gap-2">
    <div className="text-white/40 text-[10.5px] uppercase tracking-wider w-24 shrink-0 pt-0.5">{label}</div>
    <div className="text-white/85 flex-1 break-all">{String(value ?? "—")}</div>
    {copyable && value && (
      <button onClick={() => copyText(String(value))} className="text-white/30 hover:text-white transition-colors shrink-0">
        <Copy className="w-3 h-3" />
      </button>
    )}
  </div>
);
